import { signal, WritableSignal } from '@angular/core';

// Internal symbol to track deletion notifications safely
// We use this instead of 'undefined' so we can support maps that actually store 'undefined' as a value.
const REMOVED = Symbol('REMOVED');

/**
 * Class that provides a similar interface to the native Map but provides fine-grained reactivity via signals
 * Prior art: https://github.com/ngxtension/ngxtension-platform/blob/main/libs/ngxtension/collections/src/signal-map.ts
 */
export class SignalMap<TKey, TValue> {
	private readonly storage = new Map<TKey, WritableSignal<TValue | typeof REMOVED>>();
	private readonly structureNotifier = signal(0);

	constructor(entries?: readonly (readonly [TKey, TValue])[] | null) {
		if (entries) {
			for (const [key, value] of entries) {
				this.storage.set(key, signal(value));
			}
		}
	}

	public get size(): number {
		this.structureNotifier();
		return this.storage.size;
	}

	public get(key: TKey): TValue | undefined {
		const valueSignal = this.storage.get(key);

		if (valueSignal) {
			const value = valueSignal();
			if (value === REMOVED) {
				return undefined;
			}

			return value;
		}

		// Listen to notifier so that we know when the item gets added
		this.structureNotifier();
		return undefined;
	}

	public set(key: TKey, value: TValue): this {
		const existingSignal = this.storage.get(key);
		if (existingSignal) {
			existingSignal.set(value);
		} else {
			this.storage.set(key, signal(value));
			this.notifyStructureChanged();
		}

		return this;
	}

	public has(key: TKey): boolean {
		this.structureNotifier();
		return this.storage.has(key);
	}

	public delete(key: TKey): boolean {
		const existingSignal = this.storage.get(key);
		if (existingSignal) {
			// Before deleting the item, update the value to REMOVED to notify listeners of this specific key
			existingSignal.set(REMOVED);
			this.storage.delete(key);
			this.notifyStructureChanged();
			return true;
		}

		return false;
	}

	public clear(): void {
		if (this.storage.size > 0) {
			// Notify ALL value signals that they are removed
			for (const s of this.storage.values()) {
				s.set(REMOVED);
			}

			this.storage.clear();
			this.notifyStructureChanged();
		}
	}

	public keys(): TKey[] {
		this.structureNotifier();
		return Array.from(this.storage.keys());
	}

	public values(): TValue[] {
		this.structureNotifier();
		const result: TValue[] = [];
		for (const s of this.storage.values()) {
			const val = s();
			if (val !== REMOVED) {
				result.push(val as TValue);
			}
		}

		return result;
	}

	public entries(): [TKey, TValue][] {
		this.structureNotifier();
		const result: [TKey, TValue][] = [];
		for (const [key, s] of this.storage.entries()) {
			const val = s();
			if (val !== REMOVED) {
				result.push([key, val as TValue]);
			}
		}

		return result;
	}

	private notifyStructureChanged(): void {
		this.structureNotifier.update((value) => value + 1);
	}
}
